/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button, ButtonGroup, Card } from 'react-bootstrap';
import RenderWhen from '@vlinder-web/react-when-module-react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid';
import { toHeaderCase } from 'js-convert-case';
import { ApexChartWidget, TChartType } from '../../../charts-widget';
import { chartOptions } from './config/chart.config';
import './styles.css';
interface IChartSeries {
  name: string;
  data: number[];
  color?: string;
}

export interface ISalesValueWidgetProps {
  supportedPeriods: ('day' | 'week' | 'month' | 'year')[];
  backgroundColor?: string;
  isSupportPeriod?: boolean;
  headerBackgroundColor?: string;
  defaultPeriod: 'day' | 'week' | 'month' | 'year';
  title: string;
  value: string;
  chartType?: TChartType;
  percentage?: number;
  percentageLabel?: string;
  themeColor?: string;
  totalCount?: any;
  isTotalCount?: boolean;
  gridBorderColor?: string;
  salesValue: {
    day?: {
      chartSeries: IChartSeries[];
      labels: string[];
    };
    week?: {
      chartSeries: IChartSeries[];
      labels: string[];
    };
    month?: {
      chartSeries: IChartSeries[];
      labels: string[];
    };
    year?: {
      chartSeries: IChartSeries[];
      labels: string[];
    };
  };
}

const formatNumber = (number: number) => {
  return new Intl.NumberFormat('en-GB', {
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 0,
  }).format(number);
};

const getChartProps = ({ gridBorderColor, salesValue, period }: any) => {
  const options = { ...chartOptions };
  let dataWithTotal: any = [];
  let chartSeries: any;
  console.log('period for getChartProps ->', period);
  if (gridBorderColor) {
    options.grid.borderColor = gridBorderColor;
  }
  if (salesValue && salesValue[period]?.labels) {
    options.xaxis.categories = salesValue[period]?.labels;
  }
  if (salesValue && salesValue[period]?.chartSeries) {
    options.colors = salesValue[period]?.chartSeries?.map((d: any) => d.color);
    dataWithTotal = salesValue[period]?.chartSeries?.map((d: any) => ({
      ...d,
      total: d?.data?.reduce((a: any, b: any) => a + b, 0),
    }));
    dataWithTotal?.sort((a: any, b: any) => a.total + b.total);
  }
  if (salesValue && salesValue[period]?.chartSeries) {
    chartSeries = salesValue[period]?.chartSeries;
  }

  return { options, dataWithTotal, chartSeries: chartSeries, period: period };
};

export const SalesValueWidget = ({
  supportedPeriods,
  isSupportPeriod,
  defaultPeriod,
  title,
  value,
  percentage,
  isTotalCount,
  totalCount,
  percentageLabel,
  backgroundColor,
  themeColor,
  gridBorderColor,
  salesValue,
  headerBackgroundColor,
  chartType,
}: ISalesValueWidgetProps) => {
  const PercentageIcon = percentage
    ? percentage < 0
      ? ChevronDownIcon
      : ChevronUpIcon
    : null;
  const percentageColor = percentage
    ? percentage < 0
      ? 'text-danger'
      : 'text-success'
    : null;

  // const [period, setPeriod] = useState(defaultPeriod);
  const [chrtProps, setChrtProps] = useState<any>(null);
  const [options, setOptions] = useState<any>(null);
  useEffect(() => {
    const _props = getChartProps({
      gridBorderColor,
      salesValue,
      period: defaultPeriod,
    });
    setChrtProps({
      ...(_props || {}),
    });
    setOptions({
      options: _props?.options,
    });
  }, []);
  const renderSupportPeriodButton = () => {
    if (isSupportPeriod) {
      return (
        <ButtonGroup className="ms-auto">
          {supportedPeriods?.map((period, index) => (
            <Button
              variant="secondary"
              active={chrtProps?.period === period}
              onClick={handleButtonClick.bind(null, period)}
            >
              {toHeaderCase(period ?? '')}
            </Button>
          ))}
        </ButtonGroup>
      );
    }
  };

  const handleButtonClick = (period: string) => {
    const _chrtProps = getChartProps({ gridBorderColor, salesValue, period });
    setChrtProps(_chrtProps);
    setOptions({
      options: {
        ...(options || {}),
        xaxis: {
          ...(options?.xaxis || {}),
          categories: _chrtProps?.options?.xaxis?.categories,
        },
      },
    });
  };
  return (
    <Card
      className="border-2 shadow"
      style={{ backgroundColor: backgroundColor }}
    >
      <Card.Header
        // className="d-sm-flex flex-row align-items-center border-0 flex-0"
        style={{ textAlign: 'center', fontWeight: 'bold' }}
      >
        <div className="d-block mb-3 mb-sm-0">
          <RenderWhen limit={2}>
            <RenderWhen.If isTrue={!!title}>
              <h5 className="fs-5 fw-normal mb-2">{title}</h5>
            </RenderWhen.If>
            <RenderWhen.If isTrue={!!value}>
              <h3 className="fs-3 fw-extrabold">{value}</h3>
            </RenderWhen.If>
          </RenderWhen>
          <small className="d-flex align-items-center mt-2">
            <RenderWhen limit={3}>
              <RenderWhen.If isTrue={!!percentageLabel}>
                <span className="fw-normal me-2">{percentageLabel}</span>
              </RenderWhen.If>
              {/*<RenderWhen.If isTrue={!!PercentageIcon}>*/}
              {/*  <PercentageIcon className={`icon icon-xs ${percentageColor}`} />*/}
              {/*</RenderWhen.If>*/}
              <RenderWhen.If isTrue={!!percentage}>
                <span className={`fw-bold ${percentageColor}`}>
                  {percentage}%
                </span>
              </RenderWhen.If>
            </RenderWhen>
          </small>
        </div>
        {renderSupportPeriodButton()}
      </Card.Header>
      <Card.Body className="p-2">
        <div className="d-xxl-flex flex-wrap justify-content-end mt-4 mt-sm-0">
          {/* {chrtProps?.dataWithTotal?.map((d: any, index: number) => (
            <div
              key={`line-chart-${index}`}
              className="d-flex align-items-center mb-2 me-3 lh-130"
            >
               
              <span
                style={{ backgroundColor: d.color }}
                className={`dot rounded-circle me-2`}
              />
             

              <small className="fw-normal">{d.name}</small>
              <small className="fw-bold text-dark ms-1">
                {formatNumber(d.total)}
              </small>
             
              
            </div>
          ))} */}
        </div>

        {chrtProps && (
          <ApexChartWidget
            chartSeries={chrtProps?.chartSeries}
            chartOptions={options?.options}
            type={chartType}
          />
        )}
        {isTotalCount && (
          <Card.Footer style={{ textAlign: 'center', fontWeight: 'bold' }}>
            {`Grand Total - ${totalCount}`}
          </Card.Footer>
        )}
      </Card.Body>
    </Card>
  );
};

SalesValueWidget.defaultProps = {
  defaultPeriod: 'day',
  isSupportPeriod: false,
  supportedPeriods: ['day', 'week', 'month', 'year'],
  backgroundColor: 'transparent',
  headerBackgroundColor: 'transparent',
  chartType: 'bar',
  isTotalCount: false,
};
